import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton
} from '@mui/material';
import {
  Add,
  CheckCircle,
  Warning,
  Delete,
  Edit
} from '@mui/icons-material';
import api from '../lib/api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const BarrierTracker: React.FC = () => {
  const { id: agreementId } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBarrier, setEditingBarrier] = useState<any>(null);
  const [formData, setFormData] = useState({
    barrierType: 'PROCESS',
    severity: 'MEDIUM',
    description: '',
    mitigation: ''
  });

  const { data: barriers, isLoading } = useQuery({
    queryKey: ['barriers', agreementId],
    queryFn: async () => {
      const res = await api.get(`/barriers?agreementId=${agreementId}`);
      return res.data.data;
    },
    enabled: !!agreementId
  });

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await api.post(`/barriers?agreementId=${agreementId}`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['barriers', agreementId] });
      handleCloseDialog();
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res = await api.patch(`/barriers/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['barriers', agreementId] });
      handleCloseDialog();
    }
  });

  const handleOpenDialog = (barrier?: any) => {
    if (barrier) {
      setEditingBarrier(barrier);
      setFormData({
        barrierType: barrier.barrierType,
        severity: barrier.severity,
        description: barrier.description,
        mitigation: barrier.mitigation || ''
      });
    } else {
      setEditingBarrier(null);
      setFormData({
        barrierType: 'PROCESS',
        severity: 'MEDIUM',
        description: '',
        mitigation: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBarrier(null);
    setFormData({
      barrierType: 'PROCESS',
      severity: 'MEDIUM',
      description: '',
      mitigation: ''
    });
  };

  const handleSubmit = () => {
    if (editingBarrier) {
      updateMutation.mutate({ id: editingBarrier.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleMarkResolved = (barrierId: string) => {
    updateMutation.mutate({ id: barrierId, data: { resolved: true } });
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'HIGH':
        return 'error';
      case 'MEDIUM':
        return 'warning';
      case 'LOW':
        return 'info';
      default:
        return 'default';
    }
  };

  const getTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      'CONTEXTUAL': 'Context',
      'INDIVIDUAL': 'Personal',
      'INTERVENTION': 'Plan Issue',
      'PROCESS': 'Process'
    };
    return labels[type] || type;
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <Typography>Loading barriers...</Typography>
      </Box>
    );
  }

  const activeBarriers = barriers?.filter((b: any) => !b.resolved) || [];
  const resolvedBarriers = barriers?.filter((b: any) => b.resolved) || [];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Implementation Barriers</Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add
        </Button>
      </Box>

      {/* Active Barriers */}
      {activeBarriers.length > 0 && (
        <Box mb={4}>
          <Typography variant="h6" gutterBottom color="error">
            Active Barriers ({activeBarriers.length})
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            {activeBarriers.map((barrier: any) => (
              <Card key={barrier.id} variant="outlined" sx={{ borderLeft: '4px solid', borderLeftColor: 'error.main' }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                    <Box display="flex" gap={1} alignItems="center">
                      <Warning color="error" />
                      <Typography variant="subtitle1">{barrier.description}</Typography>
                    </Box>
                    <Box display="flex" gap={1}>
                      <Chip label={getTypeLabel(barrier.barrierType)} size="small" variant="outlined" />
                      <Chip
                        label={barrier.severity}
                        color={getSeverityColor(barrier.severity)}
                        size="small"
                      />
                      <IconButton size="small" onClick={() => handleOpenDialog(barrier)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  {barrier.mitigation && (
                    <Box mt={2}>
                      <Typography variant="caption" color="text.secondary" component="div">
                        <strong>Mitigation:</strong> {barrier.mitigation}
                      </Typography>
                    </Box>
                  )}
                  <Box mt={2}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<CheckCircle />}
                      onClick={() => handleMarkResolved(barrier.id)}
                    >
                      Mark Resolved
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      )}

      {/* No Active Barriers */}
      {activeBarriers.length === 0 && (
        <Alert severity="success" sx={{ mb: 3 }}>
          No active barriers. Great progress!
        </Alert>
      )}

      {/* Resolved Barriers */}
      {resolvedBarriers.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom color="text.secondary">
            Resolved Barriers ({resolvedBarriers.length})
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            {resolvedBarriers.slice(0, 5).map((barrier: any) => (
              <Card key={barrier.id} variant="outlined" sx={{ opacity: 0.7 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" gap={1} alignItems="center">
                      <CheckCircle color="success" />
                      <Typography variant="body2" sx={{ textDecoration: 'line-through' }}>
                        {barrier.description}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(barrier.resolvedAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      )}

      {/* Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingBarrier ? 'Edit Barrier' : 'Identify Barrier'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.barrierType}
                onChange={(e) => setFormData({ ...formData, barrierType: e.target.value })}
                label="Type"
              >
                <MenuItem value="CONTEXTUAL">Contextual</MenuItem>
                <MenuItem value="INDIVIDUAL">Individual</MenuItem>
                <MenuItem value="INTERVENTION">Intervention</MenuItem>
                <MenuItem value="PROCESS">Process</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Severity</InputLabel>
              <Select
                value={formData.severity}
                onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                label="Severity"
              >
                <MenuItem value="LOW">Low</MenuItem>
                <MenuItem value="MEDIUM">Medium</MenuItem>
                <MenuItem value="HIGH">High</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
            />

            <TextField
              fullWidth
              label="Mitigation Plan (Optional)"
              multiline
              rows={2}
              value={formData.mitigation}
              onChange={(e) => setFormData({ ...formData, mitigation: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!formData.description || createMutation.isPending || updateMutation.isPending}
          >
            {editingBarrier ? 'Update' : 'Add'} Barrier
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BarrierTracker;

